import { notification } from "antd";
import cuid from "cuid";
import { find, get, groupBy, union } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineDownload,
  MdOutlineDownloadForOffline,
  MdOutlineEditNote,
} from "react-icons/md";
import Button from "shared/components/Button";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import Modal from "shared/components/Modal";
import Loading from "shared/components/Spin";
import { NAME_SPACES } from "shared/locales/constants";
import {
  DOCUMENTS_TYPES,
  PROFILE_LIST,
  SUR_COMPLEMENT_WEIGHT,
} from "utils/constants";
import { downloadDocument, downloadFiles } from "utils/helpers/files";
import { STEPPER_KEYS } from "../../../..";
import File, { FILE_MODE } from "../elements/File";

const Documents = ({
  additionalInfoPercentage,
  setActive,
  sendAttachments,
  data,
  subscription,
  downloadDocuments,
  downloadSingleDocument,
  generateSubscriptionDocuments,
}) => {
  const [selectedData, setSelectedData] = useState([]);
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  const hasComplementaryProduct = find(
    get(
      subscription,
      `project.fields.upsells.${get(subscription, "insurancePlan.id")}`,
      [],
    ),
    { weight: SUR_COMPLEMENT_WEIGHT },
  );
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", {
    returnObjects: true,
  });
  const DOCUMENTS = t("SUBSCRIPTION.DOCUMENT.DOCUMENTS", {
    returnObjects: true,
  });

  const downloadAfterGenerate = (type) => {
    setLoading(true);
    generateSubscriptionDocuments({
      variables: {
        data: { id: get(subscription, "id") },
      },
      onCompleted: ({ generateSubscriptionDocuments: { attachments } }) => {
        const document = find(attachments, { type });
        if (!document) return;
        downloadSingleDocument({
          variables: { where: { id: document.id } },
          onCompleted: (_) => {
            const { document } = omitDeep(
              _.downloadSingleDocument,
              "__typename",
            );
            downloadDocument(document);
            setLoading(false);
          },
        });
      },
    });
  };

  const SECTIONS = useMemo(() => {
    const {
      DUTY_OF_ADVICE_QUOTE = [],
      DUTY_OF_ADVICE = [],
      MEMBERSHIP_FORM = [],
      SUPPLEMENTARY_DOCUMENTS = [],
      SEPA_DIRECT_DEBIT_MANDATE = [],
      // TERMINATION_LETTER = [],
      TERMINATION_MANDATE = [],
      RGPD = [],
      IPID = [],
      INFO_NOTICE = [],
      BROCHURE = [],
      STATUSES = [],
      QUOTE = [],
    } = groupBy(union(data, subscription?.attachments), "type");

    return [
      {
        title: DOCUMENTS.MEMBERSHIP_APPLICATION,
        isChecked: true,
        files: [
          ...(subscription.locked && MEMBERSHIP_FORM.length
            ? MEMBERSHIP_FORM.map(({ name, ...rest }) => ({
                ...rest,
                filename: name,
                mode: FILE_MODE.SIMPLE_FILE,
              }))
            : [
                {
                  id: cuid(),
                  filename: "BULLETIN D'ADHÉSION",
                  mode: FILE_MODE.FILE_WITH_PERCENTAGE,
                  percentageLabel: `${additionalInfoPercentage}% ${SUBSCRIPTION.DONE}`,
                  percentage: additionalInfoPercentage,
                  generate: true,
                  actions: [
                    ...(additionalInfoPercentage < 100
                      ? [
                          {
                            icon: <MdOutlineEditNote size={16} />,
                            text: "Remplir",
                            onClick: () =>
                              setActive(STEPPER_KEYS.ADDITIONAL_INFORMATION),
                          },
                        ]
                      : []),
                  ],
                  disabled: additionalInfoPercentage < 100,
                  onClick: () =>
                    downloadAfterGenerate(DOCUMENTS_TYPES.MEMBERSHIP_FORM),
                },
              ]),
          ...(get(subscription, "project.contact.fields.profile") !==
          PROFILE_LIST.RENAULT_EMPLOYEE
            ? subscription.locked && DUTY_OF_ADVICE.length
              ? DUTY_OF_ADVICE.map(({ name, ...rest }) => ({
                  ...rest,
                  filename: name,
                  mode: FILE_MODE.SIMPLE_FILE,
                }))
              : [
                  {
                    id: cuid(),
                    filename: "Devoir de conseil",
                    mode: FILE_MODE.FILE_WITH_PERCENTAGE,
                    percentageLabel: `${additionalInfoPercentage}% ${SUBSCRIPTION.DONE}`,
                    percentage: additionalInfoPercentage,
                    generate: true,
                    actions: [
                      ...(additionalInfoPercentage < 100
                        ? [
                            {
                              icon: <MdOutlineEditNote size={16} />,
                              text: "Remplir",
                              onClick: () =>
                                setActive(STEPPER_KEYS.ADDITIONAL_INFORMATION),
                            },
                          ]
                        : []),
                    ],
                    disabled: additionalInfoPercentage < 100,
                    onClick: () =>
                      downloadAfterGenerate(DOCUMENTS_TYPES.DUTY_OF_ADVICE),
                  },
                ]
            : []),
          ...(hasComplementaryProduct
            ? subscription.locked && SUPPLEMENTARY_DOCUMENTS.length
              ? SUPPLEMENTARY_DOCUMENTS.map(({ name, ...rest }) => ({
                  ...rest,
                  filename: name,
                  mode: FILE_MODE.SIMPLE_FILE,
                }))
              : [
                  {
                    id: cuid(),
                    filename: "Documents complémentaires liés à l’adhésion",
                    mode: FILE_MODE.FILE_WITH_PERCENTAGE,
                    percentageLabel: `${additionalInfoPercentage}% ${SUBSCRIPTION.DONE}`,
                    percentage: additionalInfoPercentage,
                    generate: true,
                    actions: [
                      ...(additionalInfoPercentage < 100
                        ? [
                            {
                              icon: <MdOutlineEditNote size={16} />,
                              text: "Remplir",
                              onClick: () =>
                                setActive(STEPPER_KEYS.ADDITIONAL_INFORMATION),
                            },
                          ]
                        : []),
                    ],
                    disabled: additionalInfoPercentage < 100,
                    onClick: () =>
                      downloadAfterGenerate(
                        DOCUMENTS_TYPES.SUPPLEMENTARY_DOCUMENTS,
                      ),
                  },
                ]
            : []),
          ...(subscription.locked && SEPA_DIRECT_DEBIT_MANDATE.length
            ? SEPA_DIRECT_DEBIT_MANDATE.map(({ name, ...rest }) => ({
                ...rest,
                filename: name,
                mode: FILE_MODE.SIMPLE_FILE,
              }))
            : [
                {
                  id: cuid(),
                  filename: "Mandat SEPA",
                  mode: FILE_MODE.FILE_WITH_PERCENTAGE,
                  percentageLabel: `${additionalInfoPercentage}% ${SUBSCRIPTION.DONE}`,
                  percentage: additionalInfoPercentage,
                  generate: true,
                  actions: [
                    ...(additionalInfoPercentage < 100
                      ? [
                          {
                            icon: <MdOutlineEditNote size={16} />,
                            text: "Remplir",
                            onClick: () =>
                              setActive(STEPPER_KEYS.ADDITIONAL_INFORMATION),
                          },
                        ]
                      : []),
                  ],
                  disabled: additionalInfoPercentage < 100,
                  onClick: () =>
                    downloadAfterGenerate(
                      DOCUMENTS_TYPES.SEPA_DIRECT_DEBIT_MANDATE,
                    ),
                },
              ]),
        ],
      },
      get(subscription, "project.ria") && {
        title: DOCUMENTS.TERMINATION_DOCUMENTS,
        isChecked: true,
        isRia: true,
        files: [
          // ...(subscription.locked && TERMINATION_LETTER.length
          //   ? TERMINATION_LETTER.map(({ name, ...rest }) => ({
          //       ...rest,
          //       filename: name,
          //       mode: FILE_MODE.SIMPLE_FILE,
          //     }))
          //   : [
          //       {
          //         id: cuid(),
          //         filename: "Courrier de résiliation",
          //         mode: FILE_MODE.FILE_WITH_PERCENTAGE,
          //         percentageLabel: `${additionalInfoPercentage}% ${SUBSCRIPTION.DONE}`,
          //         percentage: additionalInfoPercentage,
          //         generate: true,
          //         actions: [
          //           ...(additionalInfoPercentage < 100
          //             ? [
          //                 {
          //                   icon: <MdOutlineEditNote size={16} />,
          //                   text: "Remplir",
          //                   onClick: () =>
          //                     setActive(STEPPER_KEYS.ADDITIONAL_INFORMATION),
          //                 },
          //               ]
          //             : []),
          //         ],
          //         disabled: additionalInfoPercentage < 100,
          //         onClick: () =>
          //           downloadAfterGenerate(DOCUMENTS_TYPES.TERMINATION_LETTER),
          //       },
          //     ]),
          ...(subscription.locked && TERMINATION_MANDATE.length
            ? TERMINATION_MANDATE.map(({ name, ...rest }) => ({
                ...rest,
                filename: name,
                mode: FILE_MODE.SIMPLE_FILE,
              }))
            : [
                {
                  id: cuid(),
                  filename: "Mandat de résiliation",
                  mode: FILE_MODE.FILE_WITH_PERCENTAGE,
                  percentageLabel: `${additionalInfoPercentage}% ${SUBSCRIPTION.DONE}`,
                  percentage: additionalInfoPercentage,
                  generate: true,
                  actions: [
                    ...(additionalInfoPercentage < 100
                      ? [
                          {
                            icon: <MdOutlineEditNote size={16} />,
                            text: "Remplir",
                            onClick: () =>
                              setActive(STEPPER_KEYS.ADDITIONAL_INFORMATION),
                          },
                        ]
                      : []),
                  ],
                  disabled: additionalInfoPercentage < 100,
                  onClick: () =>
                    downloadAfterGenerate(DOCUMENTS_TYPES.TERMINATION_MANDATE),
                },
              ]),
        ],
      },
      {
        title: DOCUMENTS.QUOTES,
        files: [
          ...DUTY_OF_ADVICE_QUOTE.map(({ name, ...rest }) => ({
            ...rest,
            filename: name,
          })),
          ...QUOTE.map(({ name, ...rest }) => ({
            ...rest,
            filename: name,
          })),
        ],
      },
      {
        title: DOCUMENTS.NOTICES,
        files: [
          ...INFO_NOTICE.map(({ name, ...rest }) => ({
            ...rest,
            filename: name,
          })),
          ...IPID.map(({ name, ...rest }) => ({
            ...rest,
            filename: name,
          })),
          ...BROCHURE.map(({ name, ...rest }) => ({
            ...rest,
            filename: name,
          })),
          ...STATUSES.map(({ name, ...rest }) => ({
            ...rest,
            filename: name,
          })),
          ...RGPD.map(({ name, ...rest }) => ({
            ...rest,
            filename: name,
          })),
        ],
      },
    ].filter(Boolean);
  }, [subscription, additionalInfoPercentage]);

  const onCheck = (record, state) => {
    setSelectedData((prev) => {
      const data = state
        ? [...prev, record]
        : [...prev.filter((item) => item !== record)];
      setIsAllChecked(
        data.length ===
          SECTIONS.reduce((acc, { files = [] }) => acc + files.length, 0),
      );
      return data;
    });
  };

  const selectAll = (e) => {
    setIsAllChecked(e.target.checked);
    e.target.checked
      ? SECTIONS.map((record) =>
          record.files
            .filter(
              ({ mode, percentage }) =>
                percentage === 100 || mode !== FILE_MODE.FILE_WITH_PERCENTAGE,
            )
            .map((file) => setSelectedData((prev) => [...prev, file])),
        )
      : setSelectedData([]);
  };

  const downloadSelected = () => {
    if (
      selectedData.find(({ mode }) => mode === FILE_MODE.FILE_WITH_PERCENTAGE)
    )
      return setVisible(true);
    setLoading(true);
    const documents = selectedData.map((document) => document.id);
    downloadDocuments({
      variables: {
        data: {
          isIn: {
            id: documents,
          },
        },
      },
      onCompleted: (_) => {
        const { base64 } = omitDeep(_.download, "__typename");
        downloadFiles(base64);
        setSelectedData([]);
        setIsAllChecked(false);
        setLoading(false);
      },
    });
  };

  const download = (file) => {
    const { id, generate } = file;
    if (generate) return file.onClick();

    setLoading(true);
    downloadSingleDocument({
      variables: { where: { id } },
      onCompleted: (_) => {
        const { document } = omitDeep(_.downloadSingleDocument, "__typename");
        downloadDocument(document);
        setLoading(false);
      },
    });
  };

  const onSubmitModal = () => {
    setLoading(true);
    return generateSubscriptionDocuments({
      variables: {
        data: { id: get(subscription, "id") },
      },
      onCompleted: ({ generateSubscriptionDocuments: { base64 } }) => {
        notification.open({
          message: DOCUMENTS.CANT_EDIT,
          duration: 5,
          type: "info",
        });
        setSelectedData([]);
        setLoading(false);
        downloadFiles(base64);
        setVisible(false);
      },
    });
  };

  if (loading) return <Loading />;

  return (
    <div className="subscription-form--wrapper">
      <Modal
        mode="simple"
        type="warning"
        okText={DOCUMENTS.MODAL.OK}
        cancelText={DOCUMENTS.MODAL.CANCEL}
        onCancel={() => setVisible(false)}
        onOk={onSubmitModal}
        message={DOCUMENTS.MODAL.MESSAGE}
        open={visible}
      />
      <Form type="vertical" title={DOCUMENTS.TITLE}>
        <div className="snippet_wrapper">
          <span className="snippet_wrapper--title">
            {DOCUMENTS.SNIPPET.TITLE}
          </span>
          <span className="snippet_wrapper--description">
            {DOCUMENTS.SNIPPET.DESCRIPTION}
          </span>

          <div className="snippet_wrapper--detail">
            <div className="snippet_wrapper--detail__row">
              <span>{DOCUMENTS.SNIPPET.BY_MAIL}</span>
              <span>
                La Mobilité Mutuelle 9 rue de Clamart – 92772
                Boulogne-Billancourt cedex.
              </span>
            </div>
          </div>
        </div>
        <div className="document-list_wrapper">
          <div className="document-list_wrapper--actions">
            <div className="right_column">
              <Input
                type="checkbox"
                onChange={selectAll}
                label={DOCUMENTS.SELECT_ALL}
                checked={isAllChecked}
              />
            </div>
            <div className="left_column">
              <Button
                type="primary--link"
                alignment="center"
                onClick={downloadSelected}
                disabled={!selectedData.length}
              >
                <MdOutlineDownload size={16} />
                {DOCUMENTS.DOWNLOAD}
              </Button>
            </div>
          </div>
          {SECTIONS.map(
            (item, index) =>
              (!item.isRia ||
                item.isRia === get(subscription, "project.ria")) && (
                <div
                  className="document-list_wrapper--file-section"
                  key={`section-${index}`}
                >
                  <span className="document-list_wrapper--file-section__title">
                    {item.title}
                  </span>
                  <div className="document-list_wrapper--file-section__content">
                    {item.files.map((file, index) => (
                      <div className="file_wrapper" key={`file-${index}`}>
                        <div className="file_wrapper--metadata">
                          <div className="file_wrapper--metadata__content">
                            <Input
                              type="checkbox"
                              checked={
                                !!selectedData.find(
                                  (item) => item.id === file.id,
                                )
                              }
                              onChange={(e) => onCheck(file, e.target.checked)}
                              disabled={file.disabled}
                            />
                            <File element={file} mode={file.mode} />
                          </div>
                        </div>
                        <div className="file_wrapper--action">
                          <Button
                            disabled={file.disabled}
                            onClick={() => download(file)}
                          >
                            <MdOutlineDownloadForOffline size={20} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ),
          )}
        </div>
      </Form>
    </div>
  );
};

export default Documents;
